import { Divider, Paper, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { ExtraSmallButton } from "../../custom";
import ExpirationDownIcon from "../../../assets/icons/ExpirationDownIcon";
import ExpirationUpIcon from "../../../assets/icons/ExpirationUpIcon";
import { makeStyles } from "tss-react/mui";
import { useSelector } from "react-redux";
import { selectTradeOrders } from "../../../selectors/strategyBuilder";
import useSelectPrimaryOrUnderlyingSecurity from "../../../hooks/useSelectPrimaryOrUnderlyingSecurity";
import { selectOptionsTradeData, selectUnderlyingOptionsTradeData } from "../../../selectors/options";
import { selectUnderlyingFutureExpiries, selectUnderlyingFuturesTradeData } from "../../../selectors/futures";
import { OptionType, Security, SecurityCategory_CategoryCode } from "../../../proto/generated/security";
import { getSecurityAsync } from "../../../util/security";
import { GetOptionChainRequestV2 } from "../../../proto/generated/candle-data-depot-option-chain";
import { TimeBucket_TimeSeries } from "../../../proto/generated/common";
import { fetchOptionChainV2 } from "../../../util/options";
import { useDispatch } from "react-redux";
import { optionsTradesDataType } from "../../../pages/typings/options.typings";
import { TradeOrder, updateTradeOrders } from "../../../reducer/strategyBuilder.reducer";
import { selectOrdersHistory } from "../../../selectors/orders";
import { OrderStatus } from "../../../proto/generated/order";
import { findNearestNumber } from "../../../util/tradepanel";

const ExpiryControls: React.FC<{disabled?: boolean}> = ({disabled}) => {

    const dispatch = useDispatch();
    const {classes} = useStyles();

    const { primaryOrUnderlyingToken: underlyingToken } = useSelectPrimaryOrUnderlyingSecurity();
    const tradeOrders = useSelector(selectTradeOrders);
    const optionsTradeData = useSelector(selectOptionsTradeData);
    const optionsTradeDataRef = useRef(optionsTradeData ? optionsTradeData[underlyingToken] : null);
    const futuresData = useSelector(selectUnderlyingFuturesTradeData(underlyingToken))
    const futuresTradeDataRef = useRef(futuresData || null);

    const orderHistory = useSelector(selectOrdersHistory) || [];
    
    const orderHistoryMap = useMemo(() => orderHistory.reduce((map, order) => {
        map[order.orderId] = order;
        return map;
    }, {} as Record<string, typeof orderHistory[0]>), [orderHistory]);
    useEffect(() => {
        optionsTradeDataRef.current = optionsTradeData ? optionsTradeData[underlyingToken] : null;
    }, [optionsTradeData])
    
    const optionExpiries = useSelector(selectUnderlyingOptionsTradeData(underlyingToken))
    const futuresExpiries = useSelector(selectUnderlyingFutureExpiries(underlyingToken));

    const [decreaseEnabled, increaseEnabled] = useMemo(() => {
        let previousEnabled = true, nextEnabled = true;

        const sortedOptionExpiries = optionExpiries?.map(exp => exp.expiration.expiry.expiry).sort() ?? [];
        const sortedFutureExpiries = futuresExpiries?.current?.map(exp => exp.expiry.expiry).sort() ?? [];

        tradeOrders.forEach((t) => {
            const {security} = t;
            if (security.categoryCode === SecurityCategory_CategoryCode.FUTURES) {
                if (!sortedFutureExpiries.length) {
                    previousEnabled = false;
                    nextEnabled = false;
                    return;
                }
                const expiryIndex = sortedFutureExpiries.indexOf(security.expiry.expiry);

                if (expiryIndex === 0) previousEnabled = false;
                if (expiryIndex === sortedFutureExpiries.length - 1) nextEnabled = false;
            } else {
                if (!sortedOptionExpiries.length) {
                    previousEnabled = false;
                    nextEnabled = false;
                    return;
                }
                const expiryIndex = sortedOptionExpiries.indexOf(security.expiry.expiry);

                if (expiryIndex === 0) previousEnabled = false;
                if (expiryIndex === sortedOptionExpiries.length - 1) nextEnabled = false;
            }
        })
        return [previousEnabled, nextEnabled];
    }, [tradeOrders, optionExpiries, futuresExpiries, orderHistoryMap])


    const onExpiryChange = useCallback(async (direction: number) => {
        if (!optionsTradeDataRef.current) return;
        let currentOptionsTradeData = optionsTradeDataRef.current;
        const newExpiries: number[] = [];
        let error = false;
        tradeOrders.forEach((t) => {
            const { security } = t;
            if (!security || error) return;
            const expiry = security.expiry.expiry;
            
            const expiries = security.categoryCode === SecurityCategory_CategoryCode.OPTIONS ? (optionExpiries || []).map(exp => exp.expiration.expiry.expiry) : (futuresExpiries?.current??[]).map(exp => exp.expiry.expiry)
            
            if (!expiries.length) {
                error = true;
                return;
            }
            const index = expiries.findIndex(exp => exp === expiry)
            
            if (index === -1 || index + direction === -1 || index + direction === expiries.length) {
                error = true;
                return;
            } else {
                const newExpiry = expiries[index + direction];
                if (newExpiry && !newExpiries.includes(newExpiry)) newExpiries.push(newExpiry);
            }
        });
        
        if (error) return;
        
        const fetchExpiries = (currentOptionsTradeData || []).filter(exp => newExpiries.includes(exp.expiration.expiry.expiry) && (!exp.trades || !exp.trades.length) ).map(
            exp => exp.expiration.expiry.expiry
        )
        
        if (fetchExpiries.length) {
            const underlying = await getSecurityAsync(underlyingToken);
            if (!underlying) return;
            const payload: GetOptionChainRequestV2 = {
                security: { code: underlying.code, exchange: underlying.exchange },
                expiries: fetchExpiries,
                noOfStrikes: -1,
                requestedFields: [],
                timeBucket: {
                    timeSeries: TimeBucket_TimeSeries.DAY,
                    value: 1
                },
                sendFutures: true
            }
            const {optionsData, futuresData} = await fetchOptionChainV2(payload, dispatch, optionsTradeDataRef.current, 'both', false)
            const expiries = optionsData.reduce((acc, o) => {
                if (!o.trades?.length) return acc;
                return {
                    ...acc,
                    [o.expiration.expiry.expiry]: o
                }
            }, {} as Record<number, optionsTradesDataType>)
            currentOptionsTradeData = (currentOptionsTradeData || []).map((o) => {
                return expiries[o.expiration.expiry.expiry]?.trades?.length ? expiries[o.expiration.expiry.expiry] : o
            })

            futuresTradeDataRef.current = futuresData;
            
        }
        
        const newTradeOrders: TradeOrder[] = tradeOrders.map((t) => {
            const {security} = t;

            const expiry = security.expiry.expiry;
            let newSecurity: Security = null;
            let price = 0;

            if (orderHistoryMap[t.referenceOrderId]?.status === OrderStatus.EXECUTED) return t;

            if (security.categoryCode === SecurityCategory_CategoryCode.OPTIONS) {
                const index = currentOptionsTradeData.findIndex(t => t.expiration.expiry.expiry === expiry)
                const expiryData = currentOptionsTradeData[index + direction];
                if (!expiryData) return t;
                let newSecurityTrade = expiryData.trades.find(t => t.strikeCol.strike === security.strikePrice);
                if (!newSecurityTrade || !newSecurityTrade?.[security.optionType === OptionType.CE ? "callOption" : "putOption"]) {
                    const strikesAvailable = expiryData.trades.filter(t => t?.[security.optionType === OptionType.CE ? "callOption" : "putOption"]).map(t => t.strikeCol.strike)
                    const nearestStrike = findNearestNumber(strikesAvailable ?? [], security.strikePrice)
                    newSecurityTrade = expiryData.trades?.find(t => t.strikeCol?.strike === nearestStrike)
                    if(!newSecurityTrade) return t;
                }
                newSecurity = newSecurityTrade[security.optionType === OptionType.CE ? "callOption" : "putOption"].security;
                price = newSecurityTrade[security.optionType === OptionType.CE ? "callOption" : "putOption"].ltp
            } else if (security.categoryCode === SecurityCategory_CategoryCode.FUTURES) {
                if (futuresTradeDataRef.current) {
                    const index = futuresTradeDataRef.current.findIndex(t => t.expiry === expiry)
                    const expiryData = futuresTradeDataRef.current[index + direction];
                    if (!expiryData) return t;
                    newSecurity = expiryData.security;
                    price = expiryData.ltp
                }
            }

            if (!newSecurity) return t;
            return {
                ...t,
                security: newSecurity,
                price
            }
        });

        dispatch(updateTradeOrders(newTradeOrders));
        
    }, [underlyingToken, tradeOrders, optionExpiries, futuresExpiries, orderHistoryMap]);
    
    const onIncreaseExpiry = useCallback(() => {
        onExpiryChange(1);
    }, [onExpiryChange]);

    const onDecreaseExpiry = useCallback(() => {
        onExpiryChange(-1);
    }, [onExpiryChange])

    return  <>
                <Typography variant="caption" color={'text.secondary'}
                    style={{ margin: '5px 0' }}>EXPIRY</Typography>
                <Paper className={classes.buttonsContainer} >
                    <ExtraSmallButton data-event={'Expiry Backward Clicked'} data-event-source={'Trade'}
                        style={{ width: '50px', height: '100%' }}
                        onClick={onDecreaseExpiry}
                        disabled={!decreaseEnabled || disabled}
                        className="button-icons"
                        color={'inherit'}
                        size={'small'}>
                            <ExpirationDownIcon />
                    </ExtraSmallButton>
                    <Divider orientation="vertical" variant="middle" flexItem />
                    <ExtraSmallButton data-event={'Expiry Forward Clicked'} data-event-source={'Trade'}
                        style={{ width: '50px', height: '100%' }}
                        onClick={onIncreaseExpiry}
                        disabled={!increaseEnabled || disabled}
                        className="button-icons"
                        color={'inherit'}
                        size={'small'}>
                            <ExpirationUpIcon />
                    </ExtraSmallButton>
                </Paper>
            </>
};


export default memo(ExpiryControls);


const useStyles = makeStyles()((theme) => {
    return {
        buttonsContainer: {
            display: 'flex',
            borderRadius: '2px',
            position: 'relative',
            alignItems: 'center',
            background: theme.palette.background.default
        }
    }
})